import {inject, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {CanActivateFn, Router} from '@angular/router';
import {onAuthStateChanged} from 'firebase/auth';
import {logger, TranslateHelperService} from '@services';
import {FirebaseInitService} from '../services/shared/firebase-init.service';

const toPath = (segments: string[]): string => {
	if (!segments || segments.length === 0) {
		return '/';
	}
	return `/${segments.filter(Boolean).join('/')}`;
};

/**
 * Guard pour vérifier si l'utilisateur est authentifié.
 * Redirige vers la route spécifiée si non authentifiée.
 */
export const authGuard: (redirectTo?: string[]) => CanActivateFn = (redirectTo = ['/']) => {
	return () => {
		const firebaseInit = inject(FirebaseInitService);
		const router = inject(Router);
		const translateService = inject(TranslateHelperService);
		const platformId = inject(PLATFORM_ID);

		if (!isPlatformBrowser(platformId)) {
			return router.parseUrl(translateService.localize(toPath(redirectTo)));
		}

		const auth = firebaseInit.auth;

		return new Promise<boolean>((resolve) => {
			const unsubscribe = onAuthStateChanged(
				auth,
				(user) => {
					if (user) {
						resolve(true);
					} else {
						router.navigateByUrl(translateService.localize(toPath(redirectTo)));
						resolve(false);
					}
					unsubscribe();
				},
				(error) => {
					logger.error('Auth guard error:', error);
					router.navigateByUrl(translateService.localize(toPath(redirectTo)));
					resolve(false);
					unsubscribe();
				}
			);
		});
	};
};

/**
 * Guard pour vérifier si l'utilisateur est authentifié et a vérifié son email.
 */
export const emailVerifiedGuard: CanActivateFn = () => {
	const firebaseInit = inject(FirebaseInitService);
	const router = inject(Router);
	const translateService = inject(TranslateHelperService);
	const platformId = inject(PLATFORM_ID);

	if (!isPlatformBrowser(platformId)) {
		return router.parseUrl(translateService.localize('/verify-email'));
	}

	const auth = firebaseInit.auth;

	return new Promise<boolean>((resolve) => {
		const unsubscribe = onAuthStateChanged(
			auth,
			(user) => {
				if (user && user.emailVerified) {
					resolve(true);
				} else {
					router.navigateByUrl(translateService.localize('/verify-email'));
					resolve(false);
				}
				unsubscribe();
			},
			(error) => {
				logger.error('Email verified guard error:', error);
				router.navigateByUrl(translateService.localize('/'));
				resolve(false);
				unsubscribe();
			}
		);
	});
};

/**
 * Guard pour vérifier si l'utilisateur est authentifié et a vérifié son email,
 * ou rediriger les non connectés vers une route publique.
 */
export const emailVerifiedOrRedirectTo: (publicRoute: string[]) => CanActivateFn = (publicRoute) => {
	return () => {
		const firebaseInit = inject(FirebaseInitService);
		const router = inject(Router);
		const translateService = inject(TranslateHelperService);
		const platformId = inject(PLATFORM_ID);

		if (!isPlatformBrowser(platformId)) {
			return router.parseUrl(translateService.localize(toPath(publicRoute)));
		}

		const auth = firebaseInit.auth;

		return new Promise<boolean>((resolve) => {
			const unsubscribe = onAuthStateChanged(
				auth,
				(user) => {
					if (!user) {
						router.navigateByUrl(translateService.localize(toPath(publicRoute)));
						resolve(false);
						unsubscribe();
						return;
					}
					if (user.emailVerified) {
						resolve(true);
					} else {
						router.navigateByUrl(translateService.localize('/verify-email'));
						resolve(false);
					}
					unsubscribe();
				},
				(error) => {
					logger.error('Email verified guard error:', error);
					router.navigateByUrl(translateService.localize(toPath(publicRoute)));
					resolve(false);
					unsubscribe();
				}
			);
		});
	};
};

/**
 * Guard pour rediriger les utilisateurs authentifiés.
 * Utile pour les pages de login/register.
 */
export const redirectLoggedInTo: (redirectTo: string[]) => CanActivateFn = (redirectTo) => {
	return () => {
		const firebaseInit = inject(FirebaseInitService);
		const router = inject(Router);
		const translateService = inject(TranslateHelperService);
		const platformId = inject(PLATFORM_ID);

		if (!isPlatformBrowser(platformId)) {
			return true;
		}

		const auth = firebaseInit.auth;

		return new Promise<boolean>((resolve) => {
			const unsubscribe = onAuthStateChanged(
				auth,
				(user) => {
					if (user) {
						router.navigateByUrl(translateService.localize(toPath(redirectTo)));
						resolve(false);
					} else {
						resolve(true);
					}
					unsubscribe();
				},
				(error) => {
					logger.error('Redirect logged in guard error:', error);
					resolve(true);
					unsubscribe();
				}
			);
		});
	};
};

/**
 * Guard pour rediriger les utilisateurs avec email vérifié.
 * Laisse passer les utilisateurs non vérifiés ou non connectés.
 */
export const redirectEmailVerifiedTo: (redirectTo: string[]) => CanActivateFn = (redirectTo) => {
	return () => {
		const firebaseInit = inject(FirebaseInitService);
		const router = inject(Router);
		const translateService = inject(TranslateHelperService);
		const platformId = inject(PLATFORM_ID);

		if (!isPlatformBrowser(platformId)) {
			return true;
		}

		const auth = firebaseInit.auth;

		return new Promise<boolean>((resolve) => {
			const unsubscribe = onAuthStateChanged(
				auth,
				(user) => {
					if (user && user.emailVerified) {
						router.navigateByUrl(translateService.localize(toPath(redirectTo)));
						resolve(false);
					} else {
						resolve(true);
					}
					unsubscribe();
				},
				(error) => {
					logger.error('Redirect verified guard error:', error);
					resolve(true);
					unsubscribe();
				}
			);
		});
	};
};

